import apiClient from './client'

export interface ChatAction {
  /** confirm_actions - в ответе массовые действия, ждущие подтверждения */
  type: 'navigate_athletes' | 'confirm_actions'
  filter_ids?: string[]
  filter_label?: string
  /** ID действий для S14 (`/ai-actions/{id}`) */
  pending_action_ids?: string[]
}

// Long Polling: response when enqueueing message
export interface EnqueueMessageResponse {
  user_message_id: string
  assistant_message_id: string
  /** У повтора - текущее состояние уже зарезервированного ответа. */
  status: 'pending' | 'processing' | 'completed' | 'failed'
  /** Сообщение с этим client_message_id уже было принято: ничего не создано заново. */
  replayed?: boolean
}

/**
 * Канал вопроса: web - чат приложения (PWA, десктоп, прежний чат в
 * мини-приложениях), telegram и max - боты. Ответ наследует канал вопроса.
 */
export type ChatChannel = 'web' | 'telegram' | 'max'

export interface ChatHistoryMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  action?: ChatAction
  status: 'pending' | 'processing' | 'completed' | 'failed'
  created_at: string
  updated_at: string
  /** Нет у записей до появления канала. */
  source_channel?: ChatChannel
}

export interface AIUsageInfo {
  used: number
  limit: number
  remaining: number
}

export const chatApi = {
  /**
   * POST /api/v1/assistants/{assistantId}/chat
   * Enqueues a chat message for async processing (Long Polling).
   * Returns 202 Accepted with message_id.
   */
  enqueueMessage: async (
    assistantId: string,
    message: string,
    clientMessageId?: string
  ): Promise<EnqueueMessageResponse> => {
    // client_message_id - защита от дублей: повтор с тем же значением сервер
    // не ставит в очередь второй раз, а возвращает уже созданные записи.
    const response = await apiClient.post<EnqueueMessageResponse>(
      `/assistants/${assistantId}/chat`,
      clientMessageId ? { message, client_message_id: clientMessageId } : { message }
    )
    return response.data
  },

  /**
   * GET /api/v1/chat/messages/{messageId}
   * Одно сообщение по id: ждать конкретный ответ, не полагаясь на часы клиента.
   */
  getMessage: async (messageId: string): Promise<ChatHistoryMessage> => {
    const response = await apiClient.get<ChatHistoryMessage>(`/chat/messages/${messageId}`)
    return response.data
  },

  /**
   * GET /api/v1/assistants/{assistantId}/chat/poll?after=timestamp
   * Long Polling endpoint - waits up to 25 seconds for new messages.
   * Returns new messages or empty array on timeout.
   * channel - только разговор этого канала (и записи без канала); без него вся лента.
   */
  pollForNewMessages: async (
    assistantId: string,
    afterTimestamp: number,
    signal?: AbortSignal,
    channel?: ChatChannel
  ): Promise<ChatHistoryMessage[]> => {
    const response = await apiClient.get<ChatHistoryMessage[]>(
      `/assistants/${assistantId}/chat/poll`,
      {
        params: channel ? { after: afterTimestamp, channel } : { after: afterTimestamp },
        timeout: 30000, // 30 seconds (slightly longer than server timeout)
        signal,
      }
    )
    return response.data
  },

  /**
   * GET /api/v1/assistants/{assistantId}/chat/history
   * Returns chat history (user + assistant messages only).
   * channel - только разговор этого канала (и записи без канала); без него вся
   * лента по всем каналам, как в прежнем чате.
   */
  getHistory: async (assistantId: string, limit = 50, channel?: ChatChannel): Promise<ChatHistoryMessage[]> => {
    const response = await apiClient.get<ChatHistoryMessage[]>(
      `/assistants/${assistantId}/chat/history`,
      { params: channel ? { limit, channel } : { limit } }
    )
    return response.data
  },

  /**
   * GET /api/v1/users/me/ai-usage
   * Returns current month AI usage info.
   */
  getAIUsage: async (): Promise<AIUsageInfo> => {
    const response = await apiClient.get<AIUsageInfo>('/users/me/ai-usage')
    return response.data
  },
}
